import { useEffect, useRef, useState } from 'react'
import { Link, useRouterState } from '@tanstack/react-router'
import { Container } from '~/components/ui/Container'
import { buttonClass } from '~/components/ui/Button'
import { isActivePath } from '~/lib/nav'

const links = [
  { href: '/about-us', label: 'About Us' },
  { href: '/news', label: 'News' },
  { href: '/events', label: 'Events' },
  { href: '/campaigns', label: 'Campaigns' },
  { href: '/donate', label: 'Donate' },
] as const

/** `minimal` renders the logo only (used by focused pages like /donate). */
export function SiteHeader({ minimal = false }: { minimal?: boolean }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const [open, setOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const menuButton = useRef<HTMLButtonElement>(null)
  const closeMenu = () => setOpen(false)

  // Any navigation closes the menu, including Back/Forward, which don't go
  // through the links' onClick. (Resetting state while rendering, rather than
  // in an effect, avoids a frame with the menu still open on the new page.)
  const [menuPath, setMenuPath] = useState(pathname)
  if (menuPath !== pathname) {
    setMenuPath(pathname)
    setOpen(false)
  }

  useEffect(() => {
    if (!open) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      setOpen(false)
      menuButton.current?.focus()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open])

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  return (
    <header
      // Opaque, not glassy: Safari 26 colours its status bar from an opaque
      // sticky header at the top and falls back to white for a translucent one.
      className={`sticky top-0 z-50 border-b bg-surface transition-[border-color,box-shadow] duration-200 ${
        open
          ? 'border-fg/5'
          : scrolled
            ? 'border-fg/5 shadow-[0_1px_8px_rgba(0,0,0,0.04)]'
            : 'border-transparent'
      } nav:py-1`}
    >
      <Container
        // The 71px min-height (+1px border = the 72px bar) is what centres the
        // logo and the menu button on one line; desktop sizes itself.
        className={`flex min-h-[71px] flex-wrap items-center justify-between max-[479px]:justify-center nav:min-h-0 ${
          // Without the nav row, keep its height so the header (and logo
          // position) matches every other page.
          minimal ? 'nav:min-h-[72px]' : ''
        }`}
      >
        <Link
          to="/"
          className="block shrink-0 cursor-pointer transition-opacity hover:opacity-80"
          onClick={closeMenu}
        >
          <img
            src="/images/logo-dxe-dark.png"
            width={259}
            height={34}
            alt="DxE Logo"
            className="hidden w-[259px] max-w-[96vw] cursor-pointer md:block dark:invert"
          />
          <img
            src="/images/logo-dxe-mark.png"
            width={59}
            height={47}
            alt="DxE Direct Action Everywhere logo"
            className="w-[59px] cursor-pointer block md:hidden dark:invert"
          />
        </Link>

        {!minimal && (
          <>
            <button
              ref={menuButton}
              type="button"
              aria-label="Menu"
              aria-controls="site-nav"
              aria-expanded={open}
              onClick={() => setOpen((o) => !o)}
              className="cursor-pointer p-[18px] transition-opacity hover:opacity-60 max-[479px]:absolute max-[479px]:top-1/2 max-[479px]:-right-[13px] max-[479px]:-translate-y-1/2 nav:hidden"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                aria-hidden="true"
                className="text-fg"
              >
                <path
                  d="M3 6h18M3 12h18M3 18h18"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>

            <nav
              id="site-nav"
              aria-label="Main"
              className={`${open ? 'block' : 'hidden'} w-full bg-surface nav:flex nav:bg-transparent nav:w-auto nav:items-center`}
            >
              {links.map(({ href, label }) => {
                const active = isActivePath(pathname, href)
                return (
                  <Link
                    key={href}
                    to={href}
                    onClick={closeMenu}
                    aria-current={active ? 'page' : undefined}
                    className={`m-2 block rounded-sm px-4 py-3.5 text-center text-base leading-7 font-bold text-accent transition-colors hover:bg-surface-alt nav:text-left ${
                      active ? 'bg-surface-alt' : ''
                    }`}
                  >
                    {label}
                  </Link>
                )
              })}
              <Link
                to="/take-action"
                onClick={closeMenu}
                className={buttonClass('solid', 'm-2')}
              >
                Take Action
              </Link>
            </nav>
          </>
        )}
      </Container>
    </header>
  )
}
